import { flexRender } from "@tanstack/react-table";
import classNames from "classnames";
import React, { Fragment } from "react";
import { createPortal } from "react-dom";
import Cell from "./Cell";

const TBody = ({ table, ...props }) => {
  const BodyWrapper = props.useDivs || !props.useStyles ? "div" : "tbody";
  const TableRow = props.useDivs || !props.useStyles ? "div" : "tr";
  const TableData = props.useDivs || !props.useStyles ? "div" : "td";

  /**
   * Many expanded row components in this codebase return a `<td colSpan=...>` (often
   * wrapped in a Fragment along with a Modal). If we wrap those again in another
   * `<td>`, the DOM becomes invalid (`td` inside `td`) and the expanded content can
   * appear truncated / not full width.
   */
  const splitExpandedLayout = (node) => {
    // Returns { tdNode, extraNodes[] }
    if (!node) return { tdNode: null, extraNodes: [] };

    if (React.isValidElement(node) && node.type === "td") {
      return { tdNode: node, extraNodes: [] };
    }

    if (React.isValidElement(node) && node.type === React.Fragment) {
      const children = React.Children.toArray(node.props.children);
      const tdNode = children.find(
        (child) => React.isValidElement(child) && child.type === "td"
      );
      const extraNodes = children.filter((child) => child !== tdNode);
      return { tdNode, extraNodes };
    }

    return { tdNode: null, extraNodes: [] };
  };

  function handleTableRowClick(row) {
    const canExpand = props.rowClickExpand && row.getCanExpand();
    if (canExpand) {
      row.toggleExpanded();
    }
  }

  // Custom RowComponent from props (e.g., for drag-and-drop rows)
  const CustomRowComponent = props.RowComponent;

  return (
    <BodyWrapper className="bg-white rounded-b-lg">
      {table.getRowModel().rows.map((row, idx) => {
        const rowClass = classNames(props.rowClasses, "last:rounded-b-lg", {
          "hover:bg-gray-50": props.rowClickExpand && row.getCanExpand(),
        });

        // If a custom RowComponent is provided, use it instead of the default rendering
        if (CustomRowComponent) {
          return (
            <Fragment key={row.id}>
              <CustomRowComponent
                row={row}
                index={idx}
                TableRow={TableRow}
                TableData={TableData}
                rowClass={rowClass}
                onRowClick={() => row.getCanExpand() && handleTableRowClick(row)}
                table={table}
                {...props}
              />
              {row.getIsExpanded() &&
                typeof props.expandedLayout === "function" &&
                (props.useDivs || !props.useStyles ? (
                  <div className="border-b border-gray-200">
                    {props.expandedLayout({ row, table })}
                  </div>
                ) : (
                  (() => {
                    const expanded = props.expandedLayout({ row, table });
                    const colSpan = row.getVisibleCells().length;
                    const { tdNode, extraNodes } = splitExpandedLayout(expanded);

                    if (tdNode) {
                      return (
                        <>
                          <tr className="border-b border-gray-200">{tdNode}</tr>
                          {extraNodes?.length > 0 &&
                            typeof document !== "undefined" &&
                            createPortal(<>{extraNodes}</>, document.body)}
                        </>
                      );
                    }

                    return (
                      <tr className="border-b border-gray-200">
                        <td colSpan={colSpan}>{expanded}</td>
                      </tr>
                    );
                  })()
                ))}
            </Fragment>
          );
        }

        return (
          <Fragment key={row.id}>
            <TableRow
              onClick={() => row.getCanExpand() && handleTableRowClick(row)}
              // style={{ lineHeight: "55px", height: "55px" }}
              className={rowClass}
            >
              {row.getVisibleCells().map((cell, idx) => {
                const isCheckbox =
                  !props.hideCheckbox && cell.column.id === "select";
                const classes = props.tableBodyClasses
                  ? props.tableBodyClasses
                  : classNames(
                      "whitespace-no-wrap border-b border-gray-200",
                      props.tbClasses,
                      {
                        "w-0 max-w-0 border-r": isCheckbox,
                      }
                    );

                const content = flexRender(
                  cell.column.columnDef.cell,
                  cell.getContext()
                );

                return (
                  <TableData
                    key={cell.id}
                    style={
                      props.useTableSizes || !props.useStyles
                        ? {
                            width: !isCheckbox && cell.column.getSize(),
                          }
                        : {}
                    }
                    className={classes}
                  >
                    {isCheckbox ? (
                      <span className="flex justify-center">{content}</span>
                    ) : (
                      <Cell {...props} {...cell}>
                        {content}
                      </Cell>
                    )}
                  </TableData>
                );
              })}
            </TableRow>
            {row.getIsExpanded() &&
              typeof props.expandedLayout === "function" &&
              (props.useDivs || !props.useStyles ? (
                <div className="border-b border-gray-200">
                  {props.expandedLayout({ row, table })}
                </div>
              ) : (
                (() => {
                  const expanded = props.expandedLayout({ row, table });
                  const colSpan = row.getVisibleCells().length;
                  const { tdNode, extraNodes } = splitExpandedLayout(expanded);

                  // If the expanded renderer already provided a <td>, render it
                  // inside the <tr> and render any extra nodes (like <Modal />)
                  // via a portal so the DOM stays valid (tbody can only contain tr).
                  if (tdNode) {
                    return (
                      <>
                        <tr className="border-b border-gray-200">{tdNode}</tr>
                        {extraNodes?.length > 0 &&
                          typeof document !== "undefined" &&
                          createPortal(<>{extraNodes}</>, document.body)}
                      </>
                    );
                  }

                  return (
                    <tr className="border-b border-gray-200">
                      <td colSpan={colSpan}>{expanded}</td>
                    </tr>
                  );
                })()
              ))}
          </Fragment>
        );
      })}
    </BodyWrapper>
  );
};

export default TBody;
